/**
 * Contratto lato client dei documenti legali (Fase 1).
 *
 * Speculare al parser `scripts/lib/parse-legal-md.ts`, che vive in
 * `scripts/` perche' e' codice di build e non deve finire in nessun
 * bundle. La duplicazione dei tipi e' voluta e sorvegliata: il test
 * `scripts/__tests__/parse-legal-md.test.ts` confronta `TIPI_BLOCCO`
 * delle due parti e cade se divergono.
 *
 * NESSUN testo legale qui dentro: i documenti arrivano a runtime dal
 * registro (Fase 2), la fonte di verita' resta `docs/legal/`.
 */

/** Discriminanti dei blocchi. Allineata al parser. */
export const TIPI_BLOCCO = [
  'heading',
  'paragraph',
  'list',
  'checkbox-list',
] as const;

/**
 * Un blocco di testo renderizzabile senza parser Markdown nel client.
 *
 * `paragraph.testo` puo' contenere "\n": sono gli a capo interni al
 * paragrafo (indirizzi, righe di firma, elenchi "a) b) c)" del cartaceo).
 * Il renderer li preserva con `white-space: pre-line`, non li collassa.
 */
export type BloccoTesto =
  | { tipo: 'heading'; livello: 1 | 2 | 3; testo: string }
  | { tipo: 'paragraph'; testo: string }
  | { tipo: 'list'; voci: string[] }
  | { tipo: 'checkbox-list'; voci: string[] };

/** Valori ammessi per `consensi[].tipo` nel frontmatter. */
export const TIPI_CONSENSO = ['binario', 'checkbox', 'presa-visione'] as const;
export type TipoConsenso = (typeof TIPI_CONSENSO)[number];

/**
 * Una casella del documento cartaceo.
 *
 * `id` e' una CHIAVE DI PERSISTENZA: arriva verbatim dal frontmatter e
 * non va mai rinominato, nemmeno a fronte di un ritocco del testo
 * (invariante I6). Un testo diverso significa nuova versione del
 * documento, non un id nuovo.
 *
 * `testo` e' l'etichetta della casella, verbatim dal documento. E' sempre
 * presente sulle versioni attive (lo impone il seed); manca solo su
 * versioni storiche emesse prima di quella regola, che portano `ambito`.
 */
export interface SpecConsenso {
  id: string;
  tipo: TipoConsenso;
  testo?: string;
  obbligatorio: boolean;
  /** Presente sulle autodichiarazioni di sicurezza (dispositivi impiantabili). */
  naturaGate?: 'sicurezza';
  /** Solo per `tipo: 'binario'`. */
  etichettaSi?: string;
  etichettaNo?: string;
  /**
   * Parafrasi tecnica delle versioni storiche senza `testo` (Integrata
   * v2026-09). Resta leggibile per mostrare cio' che il paziente ha visto.
   */
  ambito?: string;
}

export interface DocumentoLegale {
  documentoId: string;
  versione: string;
  hashContenuto: string;
  tipo: string;
  obbligatorio: boolean;
  bloccanteSeRifiutato: boolean;
  gateClinico?: boolean;
  naturaPrestazione?: string;
  blocchi: BloccoTesto[];
  consensi: SpecConsenso[];
  /**
   * Etichette di navigazione dal registro (docs/legal/registro.yaml), solo
   * sui documenti ATTIVI. Assenti su una versione storica e durante la
   * finestra di deploy in cui il registro non le ha ancora.
   */
  titoloBreve?: string;
  sommario?: string;
}

export type EsitoConsenso = 'accettato' | 'rifiutato' | 'parziale';

/** Una casella spuntata (o non spuntata) dall'interessato. */
export interface VoceConsenso {
  consensoId: string;
  accepted: boolean;
}

/**
 * Esito di un documento a partire dalle caselle e dalla loro specifica.
 *
 * Funzione pura, e l'UNICO punto in cui l'esito viene derivato: portale,
 * gestionale e Cloud Function devono concordare, e l'unico modo per
 * garantirlo e' non riscrivere la regola una seconda volta.
 *
 * Non ha default silenziosi. Una voce che non corrisponde ad alcuna
 * specifica, o una specifica che nessuna voce copre, sono errori: un
 * payload incompleto non deve tradursi in un esito plausibile ma falso.
 */
export function deriveEsito(
  voci: ReadonlyArray<VoceConsenso>,
  spec: ReadonlyArray<SpecConsenso>
): EsitoConsenso {
  if (spec.length === 0) {
    throw new Error('deriveEsito: specifica vuota.');
  }

  const perId = new Map<string, boolean>();
  for (const voce of voci) {
    if (perId.has(voce.consensoId)) {
      throw new Error(
        `deriveEsito: voce duplicata per consensoId "${voce.consensoId}".`
      );
    }
    perId.set(voce.consensoId, voce.accepted);
  }

  const idsSpec = new Set(spec.map((s) => s.id));
  for (const consensoId of perId.keys()) {
    if (!idsSpec.has(consensoId)) {
      throw new Error(
        `deriveEsito: consensoId "${consensoId}" non presente nella specifica del documento.`
      );
    }
  }

  const mancanti = spec.filter((s) => !perId.has(s.id)).map((s) => s.id);
  if (mancanti.length > 0) {
    throw new Error(
      `deriveEsito: voci mancanti per ${mancanti.join(', ')}.`
    );
  }

  let qualcheFacoltativaRifiutata = false;
  for (const s of spec) {
    const accepted = perId.get(s.id) === true;
    if (!accepted) {
      if (s.obbligatorio) return 'rifiutato';
      qualcheFacoltativaRifiutata = true;
    }
  }

  return qualcheFacoltativaRifiutata ? 'parziale' : 'accettato';
}

/** Titolo integrale: primo heading del documento, altrimenti l'id. */
export function titoloDocumento(
  doc: Pick<DocumentoLegale, 'blocchi' | 'documentoId'>
): string {
  const heading = doc.blocchi.find((b) => b.tipo === 'heading');
  if (heading && heading.tipo === 'heading' && heading.testo.trim() !== '') {
    return heading.testo;
  }
  return doc.documentoId;
}

/**
 * Titolo della card: `titoloBreve` del registro se c'e', altrimenti il
 * titolo integrale. Su una versione storica coincide con il titolo com'era.
 */
export function titoloCard(
  doc: Pick<DocumentoLegale, 'blocchi' | 'documentoId' | 'titoloBreve'>
): string {
  if (doc.titoloBreve && doc.titoloBreve.trim() !== '') return doc.titoloBreve;
  return titoloDocumento(doc);
}

/**
 * Etichetta di una casella: `testo`, oppure `ambito` sulle versioni storiche
 * che non avevano `testo`. `null` se nessuna delle due: non se ne inventa una.
 */
export function etichettaConsenso(
  spec: Pick<SpecConsenso, 'testo' | 'ambito'>
): string | null {
  if (spec.testo && spec.testo.trim() !== '') return spec.testo;
  if (spec.ambito && spec.ambito.trim() !== '') return spec.ambito;
  return null;
}
